import {
  BaseMaskCreateAndDeployResponse,
  DataAccessGroupInfo,
  PolicyApiCreateAndDeployBaseMaskPoliciesRequest,
  CloudAccountApiListAbacAccountsRequest,
  DataGroupApiGetDataAccessGroupsRequest,
  DataGroupApiGetIdentitiesUsingDataRoleRequest,
  DataGroupApiListDataGroupsRequest,
  IdentityInfo,
  MultiBlockPolicyPreviewResponse,
  MultiBlockPolicyView,
  PageCloudAccountResponse,
  PageDataGroupListResponse,
  PagePolicyResponse,
  PolicyApiCreateAndDeployMultiBlockPolicyRequest,
  PolicyApiDeletePolicyRequest,
  PolicyApiGetPolicyRequest,
  PolicyApiGetMultiBlockPolicyRequest,
  PolicyApiListPoliciesRequest,
  PolicyApiPreviewMultiBlockPolicyDdlRequest,
  PolicyApiUpdateAndDeployMultiBlockPolicyRequest,
  PolicyPreviewResponse,
  PolicyResponse,
  ResourceApiGetProviderStructureRequest,
  ResourceApiListSnowflakeTagsRequest,
  TagsResponse,
} from '@ariksa/abac/api';
import { InsightV2ApiInsightV2AllRequest } from '@ariksa/inventory-core/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { QueryAction, QueryStatus } from 'services/types';
import { PagedQueryState } from 'services/utils/PagedQueryState';
import { QueryState } from 'services/utils/QueryState';

import { PageInfo } from 'components/Navigation';

import { createSlice } from '../../../../utils/@reduxjs/toolkit';

import { ContainerState } from './types';

// The initial state of the PolicyHub container
export const initialState: ContainerState = {
  cloudAccounts: QueryState.init([]),
  dataGroups: PagedQueryState.init([]),
  baseMaskPolicyAction: QueryState.init({} as BaseMaskCreateAndDeployResponse),
  policyAction: QueryState.init({}),
  dataPolicies: PagedQueryState.init([]),
  dataPolicy: QueryState.init({} as PolicyResponse),
  multiBlockPolicy: QueryState.init({} as MultiBlockPolicyView),
  structure: QueryState.init({}),
  resources: {},
  tagKeyValues: QueryState.init([]),
  generatePolicy: QueryState.init({} as PolicyPreviewResponse),
  identitiesUsingDataRole: QueryState.init([]),
  dataGroupRoles: {},
};

const maskingPoliciesSlice = createSlice({
  name: 'maskingPolicies',
  initialState,
  reducers: {
    getCloudAccounts(
      state,
      action: QueryAction<
        PageCloudAccountResponse,
        CloudAccountApiListAbacAccountsRequest
      >,
    ) {
      state.cloudAccounts = QueryState.next(state.cloudAccounts, action, {
        mapData: r => r?.items,
      });
    },

    createAndDeployBaseMaskPolicies(
      state,
      action: QueryAction<
        BaseMaskCreateAndDeployResponse,
        PolicyApiCreateAndDeployBaseMaskPoliciesRequest
      >,
    ) {
      state.baseMaskPolicyAction = QueryState.next(
        state.baseMaskPolicyAction,
        action,
      );
    },

    getPolicyById(
      state,
      action: QueryAction<PolicyResponse, PolicyApiGetPolicyRequest>,
    ) {
      state.dataPolicy = QueryState.next(state.dataPolicy, action);
    },

    getMultiBlockPolicy(
      state,
      action: QueryAction<
        MultiBlockPolicyView,
        PolicyApiGetMultiBlockPolicyRequest
      >,
    ) {
      state.multiBlockPolicy = QueryState.next(state.multiBlockPolicy, action);
    },

    getDataPolicies(
      state,
      action: QueryAction<PagePolicyResponse, PolicyApiListPoliciesRequest>,
    ) {
      state.dataPolicies = PagedQueryState.next(state.dataPolicies, action, {
        mapTotalCount: r => r.total ?? 0,
        mapData: r => r.items,
      });
    },
    updatePoliciesPagination(state, action: PayloadAction<PageInfo>) {
      state.dataPolicies.page.info = action.payload;
    },

    createPolicy(
      state,
      action: QueryAction<any, PolicyApiCreateAndDeployMultiBlockPolicyRequest>,
    ) {
      state.policyAction = QueryState.next(state.policyAction, action);
    },
    updateAndDeployMultiBlockPolicy(
      state,
      action: QueryAction<any, PolicyApiUpdateAndDeployMultiBlockPolicyRequest>,
    ) {
      state.policyAction = QueryState.next(state.policyAction, action);
    },
    deletePolicy(
      state,
      action: QueryAction<any, PolicyApiDeletePolicyRequest>,
    ) {
      state.policyAction = QueryState.next(state.policyAction, action);
    },
    getStructure(
      state,
      action: QueryAction<
        Record<string, any>,
        ResourceApiGetProviderStructureRequest
      >,
    ) {
      state.structure = QueryState.next(state.structure, action);
    },

    getResources(
      state,
      action: QueryAction<
        Record<string, any>[],
        InsightV2ApiInsightV2AllRequest
      >,
    ) {
      const resourceType = action.payload.q.insightV2RequestBase.resource_type;
      if (!state.resources[resourceType!]) {
        state.resources[resourceType!] = QueryState.init({} as any, {
          status: QueryStatus.pending,
        });
        return;
      } else {
        state.resources[resourceType!] = QueryState.next(
          state.resources[resourceType!],
          action,
        );
      }
    },

    getTagKeyValues(
      state,
      action: QueryAction<TagsResponse, ResourceApiListSnowflakeTagsRequest>,
    ) {
      state.tagKeyValues = QueryState.next(state.tagKeyValues, action, {
        mapData: r => r.tags ?? [],
      });
    },

    generatePolicy(
      state,
      action: QueryAction<
        MultiBlockPolicyPreviewResponse,
        PolicyApiPreviewMultiBlockPolicyDdlRequest
      >,
    ) {
      state.generatePolicy = QueryState.next(state.generatePolicy, action);
    },

    getIdentitiesUsingDataRole(
      state,
      action: QueryAction<
        IdentityInfo[],
        DataGroupApiGetIdentitiesUsingDataRoleRequest
      >,
    ) {
      state.identitiesUsingDataRole = QueryState.next(
        state.identitiesUsingDataRole,
        action,
      );
    },

    getDataGroups(
      state,
      action: QueryAction<
        PageDataGroupListResponse,
        DataGroupApiListDataGroupsRequest
      >,
    ) {
      state.dataGroups = PagedQueryState.next(state.dataGroups, action, {
        mapData: r => r.items ?? [],
        mapTotalCount: r => r.total ?? 0,
      });
    },

    getDataGroupRoles(
      state,
      action: QueryAction<
        DataAccessGroupInfo[],
        DataGroupApiGetDataAccessGroupsRequest
      >,
    ) {
      const dataGroupId = action.payload.q.dataGroupId;
      if (!state.dataGroupRoles[dataGroupId]) {
        state.dataGroupRoles[dataGroupId] = QueryState.init(
          [] as DataAccessGroupInfo[],
          {
            status: QueryStatus.pending,
          },
        );
        return;
      } else {
        state.dataGroupRoles[dataGroupId] = QueryState.next(
          state.dataGroupRoles[dataGroupId],
          action,
        );
      }
    },
  },
});

export const { actions, reducer, name: sliceKey } = maskingPoliciesSlice;
